const defaultArgs: IdleDeadline = {
  didTimeout: false,
  timeRemaining: () => Infinity,
}

/**
 * Simple wrapper around requestIdleCallback that provides a fallback to setTimeout
 * on server-side or if requestIdleCallback is not supported.
 *
 * - on the server-side, it calls the callback immediately
 * - on the client-side, it uses the real `requestIdleCallback` if available,
 *   otherwise it uses `setTimeout`.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/requestIdleCallback}
 */
export function requestIdleCallback(
  ...[cb, ...args]: Parameters<typeof window.requestIdleCallback>
) {
  // server-side, call the callback immediately
  // Also, in Vitest this is failing because Window is not defined
  // by the time the setTimeout is called
  if (typeof window === 'undefined' || process.env.VITEST === 'true') {
    return cb(defaultArgs)
  }

  if (window.requestIdleCallback) {
    return window.requestIdleCallback(cb, ...args)
  }

  // Fallback to setTimeout if requestIdleCallback is not supported
  // If this simple fallback is not enough, please consider using a polyfill
  return setTimeout(() => {
    cb(defaultArgs)
  }, 0)
}

/**
 * Companion of our custom implementation of `requestIdleCallback`
 * that cancels the timeout, using the same mechanism:
 *
 * - on the server-side, it does nothing
 * - on the client-side, it uses the real `cancelIdleCallback` if available,
 *   otherwise it uses `clearTimeout`.
 *
 * @see {@link requestIdleCallback}
 */
export function cancelIdleCallback(id: number): void {
  if (typeof window === 'undefined') {
    return
  }

  if (window.cancelIdleCallback) {
    window.cancelIdleCallback(id)

    return
  }

  clearTimeout(id)
}
